import { useEffect, useMemo, useState } from "react";

import { DataLoadError } from "components/ui";
import { Notice, NoticeText } from "components/ui/Notice";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { useHealthlinkVaccines } from "features/immunizations/hooks/useHealthlinkVaccines";
import { usePatientSchedule } from "features/immunizations/hooks/usePatientSchedule";
import {
  findCatalogueVaccineForSlot,
  groupScheduleSlots,
} from "features/immunizations/utils/schedule";

import { ScheduleGroup } from "./ScheduleGroup";
import s from "./ScheduleTab.module.css";

/**
 * What a patient is due, as opposed to what they have been given (KIN-1842).
 *
 * The other half of the immunisations view: the history tab is the record, this is the
 * outstanding work. Both sit under the same heading and the same Add vaccine button,
 * because a clinician switching between them is answering one question.
 *
 * Everything shown is the backend's judgement (KIN-1811) — due dates run from the last dose
 * actually given, and slots the school programme delivers are marked as such. Nothing here
 * recomputes any of it. What this adds is the order it is read in, and the two caveats that
 * keep the list honest.
 */
export const ScheduleTab = ({ patientId, onRecordDose }) => {
  const {
    data: schedule,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = usePatientSchedule(patientId);

  const groups = useMemo(
    () => groupScheduleSlots(schedule?.slots),
    [schedule?.slots]
  );

  // One group at a time. Stacked, the twenty-odd slots ran to several screens and gave
  // "Given" the same weight as "Overdue", when only the first two are why anyone opened
  // this. The counts stay visible on the tabs, so nothing is hidden — only deferred.
  const [activeGroupId, setActiveGroupId] = useState(null);

  // Defaults to the most urgent group that exists, and follows the data: a patient with
  // nothing overdue should land on "Due now", not on an empty tab.
  useEffect(() => {
    setActiveGroupId((current) =>
      groups.some((group) => group.id === current)
        ? current
        : groups[0]?.id ?? null
    );
  }, [groups]);

  const activeGroup = groups.find((group) => group.id === activeGroupId);

  // The catalogue, so a due dose can open the form on the right product. Where the course
  // has two — a 6-in-1 or an RSV weight band — this resolves to nothing and the clinician
  // picks, which is the correct outcome rather than a gap.
  const { data: healthlinkVaccines } = useHealthlinkVaccines();

  const handleRecord = (slot) =>
    onRecordDose?.({
      vaccineCodeId: findCatalogueVaccineForSlot(slot, healthlinkVaccines)?.id,
      doseNumberCode: String(slot.doseNumber),
    });

  return (
    <div className={s.tab}>
      {!!schedule?.scheduleName && (
        <p className={s.scheduleName}>{schedule.scheduleName}</p>
      )}

      {!!isLoading && <p className={s.message}>Loading the schedule…</p>}

      {/* An unreachable schedule must never read as "nothing is due" — the same failure
          KIN-1616 fixed on the clinical screens. */}
      {!!isError && (
        <DataLoadError
          label="this patient's immunisation schedule"
          text="This is not a statement that nothing is due."
          onRetry={refetch}
          isRetrying={isFetching}
        />
      )}

      {!!schedule?.unavailableReason && (
        <Notice tone="info" testId="schedule-unavailable">
          <NoticeText>{schedule.unavailableReason}</NoticeText>
        </Notice>
      )}

      {/* Before the list, not after it. No immunisation history migrates into this system,
          so a child can look due for everything they have already had — and a clinician
          acting on that would recall a fully vaccinated family.

          `info` rather than `blocking`: Notice reserves the alert role for "this cannot be
          submitted", and interrupting a screen reader for a caveat is the wrong trade. The
          wording carries the weight instead. */}
      {!!schedule?.historyMayBeIncomplete && !schedule?.unavailableReason && (
        <Notice tone="info" testId="schedule-history-caveat">
          <NoticeText>
            {schedule.historyCaveat ||
              "This practice's record may not cover the patient's whole vaccination history, so an outstanding dose below may already have been given elsewhere."}
          </NoticeText>
        </Notice>
      )}

      {!isLoading && !isError && !schedule?.unavailableReason && !groups.length && (
        <p className={s.message}>
          Nothing outstanding on this patient&apos;s schedule.
        </p>
      )}

      {!!groups.length && (
        <TabSwitcher
          value={activeGroupId}
          onChange={setActiveGroupId}
          options={groups.map((group) => ({
            value: group.id,
            label: group.title,
            count: group.slots.length,
          }))}
          fullWidth
        />
      )}

      {!!activeGroup && (
        <ScheduleGroup group={activeGroup} onRecord={handleRecord} />
      )}
    </div>
  );
};
